/**
 * Tabs — APG tablist pattern with automatic activation.
 *
 * DOM contract:
 *   <ul data-tabs id="{id}" role="tablist">
 *     <li class="tabs-title" role="presentation">
 *       <a role="tab" href="#{panelId}" aria-controls="{panelId}" …>
 *
 *   <div data-tabs-content="{id}">
 *     <div class="tabs-panel" id="{panelId}" role="tabpanel" …>
 *
 * Behaviour:
 * - Click or arrow keys activate the tab, focus follows the selected tab.
 * - Home / End jump to first / last tab.
 * - Active tab click updates `location.hash` via pushState (no scroll jump).
 * - On page load and on `hashchange`, the tab whose panel id matches the hash
 *   is activated (no focus steal).
 * - A `.tabs-indicator` div is injected behind the tabs and slid/resized to the
 *   active tab, so switching animates the active fill to its new position and
 *   width (and height, in the stacked mobile layout). Progressive enhancement:
 *   without JS the active tab keeps its solid-background fallback (CSS).
 */

const ACTIVE_CLASS = 'is-active';
const tabsets = [];

function init() {
    document.querySelectorAll('[data-tabs]').forEach((tablist) => {
        const id = tablist.id;
        if (!id) return;
        const contentContainer = document.querySelector('[data-tabs-content="' + CSS.escape(id) + '"]');
        if (!contentContainer) return;

        const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
        const panels = Array.from(contentContainer.querySelectorAll('[role="tabpanel"]'));
        if (!tabs.length || tabs.length !== panels.length) return;

        const activeIndex = Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
        const set = { tabs, panels, activeIndex, indicator: null, wrapper: null };

        // Inject the sliding indicator as a sibling of the <ul> (keeps the list
        // markup valid — a tablist holds only its tabs). The wrapper is its
        // positioned containing block (see 85_blocks.sass).
        const wrapper = tablist.closest('.tabs-wrapper') || tablist.parentElement;
        if (wrapper) {
            const indicator = document.createElement('div');
            indicator.className = 'tabs-indicator';
            indicator.setAttribute('aria-hidden', 'true');
            wrapper.insertBefore(indicator, wrapper.firstChild);
            tablist.classList.add('has-indicator');
            set.indicator = indicator;
            set.wrapper = wrapper;
            moveIndicator(set, false); // place on the initially-active tab, no slide

            // Reposition (without animating) whenever the tablist reflows:
            // font swap, the column↔row breakpoint flip, container resize.
            if (window.ResizeObserver) {
                new ResizeObserver(() => moveIndicator(set, false)).observe(tablist);
            }
        }

        tabsets.push(set);

        tabs.forEach((tab, index) => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                activate(set, index, { updateHash: true, focus: true });
            });
            tab.addEventListener('keydown', (e) => onKeydown(e, set, index));
        });
    });

    if (!tabsets.length) return;

    applyHash(false);
    window.addEventListener('hashchange', () => applyHash(true));

    // Recalculate every indicator on window resize. The per-tablist
    // ResizeObserver above catches size changes to the list itself, but a
    // viewport resize can shift tab positions (surrounding layout reflow, the
    // column↔row breakpoint flip) without resizing the list's own box — and it
    // also covers browsers without ResizeObserver support. Snap, don't slide.
    window.addEventListener('resize', onResize);
}

// Coalesce bursts of resize events into one reposition per frame.
let resizeRaf = null;
function onResize() {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        tabsets.forEach((set) => moveIndicator(set, false));
    });
}

function applyHash(animate) {
    const hash = location.hash.slice(1);
    if (!hash) return;
    // The hash may point at a panel itself, or at any element nested inside one
    // (e.g. an artist toggle) — activate the panel that contains the target.
    const target = document.getElementById(hash);
    tabsets.forEach((set) => {
        const index = set.panels.findIndex((p) => p.id === hash || (target && p.contains(target)));
        if (index >= 0) activate(set, index, { updateHash: false, focus: false, animate });
    });
}

function activate(set, index, opts) {
    set.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        const li = tab.closest('.tabs-title');
        if (li) li.classList.toggle(ACTIVE_CLASS, selected);
    });
    set.panels.forEach((panel, i) => {
        const selected = i === index;
        if (selected) {
            // Un-hide first (display:none → block), then force a reflow so the
            // panel gets a layout pass at opacity:0 before .is-active flips it to
            // 1 — otherwise both land in the same frame and the fade is skipped.
            panel.removeAttribute('hidden');
            void panel.offsetWidth;
            panel.classList.add(ACTIVE_CLASS);
        } else {
            panel.classList.remove(ACTIVE_CLASS);
            panel.setAttribute('hidden', '');
        }
    });

    set.activeIndex = index;
    moveIndicator(set, opts.animate !== false);

    if (opts.updateHash) {
        const panelId = set.panels[index].id;
        if (panelId && location.hash !== '#' + panelId) {
            history.pushState(null, '', '#' + panelId);
        }
    }
    if (opts.focus) set.tabs[index].focus();

    // Panel is now un-hidden — let nested modules (e.g. toggle.js deep-linking)
    // reveal a hash target inside it now that it has layout.
    window.dispatchEvent(new CustomEvent('tabs:activated', { detail: { panel: set.panels[index] } }));
}

/**
 * Size and position the indicator over the active tab. When `animate` is false
 * the move is applied with transitions suppressed (initial placement, reflow) so
 * it doesn't slide in from a stale position.
 */
function moveIndicator(set, animate) {
    const tab = set.tabs[set.activeIndex];
    if (!tab || !set.indicator || !set.wrapper) return;

    const wrapRect = set.wrapper.getBoundingClientRect();
    const r = tab.getBoundingClientRect();
    const ind = set.indicator;

    if (!animate) ind.style.transition = 'none';
    // Ceil the subpixel rect dimensions — iOS rounds fractional widths down when
    // rendering, which leaves a 1px gap on the right edge of the indicator.
    ind.style.width = Math.ceil(r.width + 1) + 'px';
    ind.style.height = Math.ceil(r.height) + 'px';
    // Round the offsets to whole pixels so the transform never lands on a
    // fractional value (e.g. 5.345px), which blurs the indicator's edges.
    ind.style.transform = 'translate(' + Math.round(r.left - wrapRect.left - .5) + 'px, ' + Math.round(r.top - wrapRect.top) + 'px)';
    if (!animate) {
        void ind.offsetWidth; // flush the no-transition write before re-enabling
        ind.style.transition = '';
    }

    ind.classList.add('is-ready');
}

function onKeydown(e, set, currentIndex) {
    let newIndex = null;
    switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
            newIndex = (currentIndex - 1 + set.tabs.length) % set.tabs.length;
            break;
        case 'ArrowRight':
        case 'ArrowDown':
            newIndex = (currentIndex + 1) % set.tabs.length;
            break;
        case 'Home':
            newIndex = 0;
            break;
        case 'End':
            newIndex = set.tabs.length - 1;
            break;
        default:
            return;
    }
    e.preventDefault();
    activate(set, newIndex, { updateHash: true, focus: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
